import React, { useMemo, useState } from 'react';
import { Teacher } from '../types';
import ApiService from '../services/api';
import './ClassroomTransferButton.css';
import { processedClassroomData, useClassroomData } from '../data/classroomData';

interface ClassroomTransferButtonProps {
  teacher: Teacher;
  currentClassroomId: string;
}

const ClassroomTransferButton: React.FC<ClassroomTransferButtonProps> = ({ 
  teacher, 
  currentClassroomId 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const classroomData = useClassroomData().data;
  const {classroomOptions} = useMemo(()=> processedClassroomData(classroomData), [classroomData]);
  const refreshData = useClassroomData().refetch

  // Filter out the current classroom from options
  const availableClassrooms = classroomOptions.filter(
    classroom => classroom.id !== currentClassroomId
  );

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(true);
    setError(null);
  };

  const handleClose = () => {
    setIsOpen(false);
    setError(null);
  };

  const handleTransfer = async (targetClassroomId: string) => {
    if (!teacher.sc_id) {
      setError('Teacher does not have a SmartCare ID');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await ApiService.checkInTeacher({
        teacherId: teacher.sc_id,
        classroomId: targetClassroomId
      });
      
      // Close modal and refresh classroom data
      setIsOpen(false);
      await refreshData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to transfer teacher');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't show the button if teacher has no SmartCare ID
  if (!teacher.sc_id) {
    return null;
  }

  return (
    <span className="classroom-transfer-btn-component">
      <span 
        className="transfer-icon-button" 
        title="Transfer to another classroom"
        onClick={handleOpen}
      />
      
      {isOpen && (
        <div className="modal-overlay" onClick={handleClose}>
          <div className="transfer-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Transfer {teacher.name}</h3>
              <button className="close-button" onClick={handleClose}>×</button>
            </div>
            
            <div className="modal-body">
              {error && <div className="error-message">{error}</div>}
              
              <p>Select a classroom to transfer to:</p>
              
              {availableClassrooms.length > 0 ? (
                <div className="classroom-list">
                  {availableClassrooms.map(classroom => (
                    <button
                      key={classroom.id}
                      className="classroom-option"
                      onClick={() => handleTransfer(classroom.id)}
                      disabled={isLoading}
                    >
                      {classroom.name}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="no-options">No other classrooms available</p>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-button" 
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
};

export default ClassroomTransferButton;
